import { Button, Popover, Portal, Overlay, MantineColor, Loader, Stack, Group, Checkbox } from '@mantine/core';
import { useEvent, useToggle } from '@root/Services/EventEmitter';
import { Tags } from 'tabler-icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SectionedPopover, SectionedPopoverToolbar } from '@root/Design/Primitives';
import { TagEditorSettings, TagKeyCombobox, TagValueCombobox, useTaggingStatus } from './ResourceTags';
import { useDi } from '@root/Services/DI';
import { DelegatedTagAuthService } from '@root/Services/Resources/DelegatedTagAuthService';
import { GcpAuthService } from '@root/Services/Connections/GcpAuthService';
import { CreateLabelKey, validateLabelKey, validateLabelValue } from '../Gcp/LabelUtils';
import { TagResourcesJob } from '@apis/Resources/model';
import { InlineTaggingCreditBalance } from '@root/Site/TagManager/Components/InlineTaggingCreditBalance';

export function AddLabelButton({ settings }: { settings: TagEditorSettings }) {
    const tagAuthSvc = useDi(DelegatedTagAuthService);
    const [opened, { toggle, close }] = useToggle(false);

    const popoverProps = useMemo(
        () => ({
            withArrow: true,
            arrowSize: 10,
            position: 'bottom' as const,
            withinPortal: true,
            shadow: 'md',
            closeOnClickOutside: false,
            closeOnEscape: false,
            offset: 0,
        }),
        []
    );

    if (settings.resource.CloudPlatform !== 'Gcp' || !tagAuthSvc.serviceEnabled(settings.resource, 'label')) {
        return null;
    }

    return (
        <>
            <Portal>{opened ? <Overlay opacity={0} onClick={close} /> : null}</Portal>
            <Popover {...popoverProps} onClose={close} opened={opened}>
                <Popover.Target>
                    <Button size="xs" variant="outline" leftIcon={<Tags strokeWidth={1} size={16} />} onClick={toggle} ml="xs">
                        Add Label
                    </Button>
                </Popover.Target>
                <SectionedPopover sx={{ width: 300 }}>
                    <AddLabelDialog settings={settings} onClose={close} />
                </SectionedPopover>
            </Popover>
        </>
    );
}

export function AddLabelDialog({ settings, onClose }: { settings: TagEditorSettings; onClose: () => void }) {
    const tagAuthSvc = useDi(DelegatedTagAuthService);
    const gcpAuthSvc = useDi(GcpAuthService);
    const [keyText, setKeyText] = useState<string>('');
    const [valueText, setValueText] = useState<string>('');
    const { resource, onChanging, inlineTagEditSvc } = settings;
    const { state, showFailure } = useTaggingStatus(resource, CreateLabelKey(keyText), inlineTagEditSvc);
    const [overwrite, setOverwrite] = useState(false);
    const [noCredits, setNoCredits] = useState(true);
    useEvent(onChanging);

    const { isAuthenticating, authError, authenticate, clearError } = tagAuthSvc.useLabelingAuth();

    const keyError = useMemo(() => validateLabelKey(keyText), [keyText]);
    const valueError = useMemo(() => validateLabelValue(valueText), [valueText]);

    const onClick = useCallback(async () => {
        clearError();
        const authResult = await authenticate(resource);
        if (!authResult || !authResult.success) {
            return;
        }
        if (state === 'blank') {
            const { CloudPlatform, Id: ResourceId, ResourceType } = resource;
            const delegated = authResult.delegated;

            const labelKey = CreateLabelKey(keyText);

            const change: TagResourcesJob = {
                AddTags: [{ Key: labelKey, Value: valueText }],
                ResourceIds: [{ CloudPlatform, ResourceId, ResourceType }],
                OverwriteConflicts: overwrite,
                DelegateAuthentication: delegated,
                AccessToken: delegated ? gcpAuthSvc.getAccessToken() : undefined,
            };
            const progress = inlineTagEditSvc.update(settings.resource, change, labelKey).finally(onChanging.emit);
            onChanging.emit();
            await progress;
            const status = inlineTagEditSvc.getStatus(resource, labelKey);
            if (status?.status === 'done') {
                onClose();
            }
        } else if (state === 'failed') {
            showFailure?.();
            onChanging.emit();
            onClose();
        }
    }, [state, keyText, valueText, showFailure, overwrite, resource]);

    const existingValue = resource.CsTags?.[keyText];
    const addDisabled = noCredits
        ? true
        : state === 'blank'
        ? !keyText || !valueText || !!keyError || !!valueError || (!!existingValue && !overwrite)
        : state === 'started';
    const addLbl =
        state === 'done'
            ? 'Label Added'
            : state === 'failed'
            ? 'View Failure'
            : state === 'started'
            ? 'Adding Label...'
            : existingValue
            ? 'Update Label'
            : 'Add Label';
    const addColor: MantineColor | undefined = state === 'failed' ? 'error' : undefined;
    const cancelDisabled = state !== 'blank';
    const cancelLbl = state === 'blank' || state === 'started' ? 'Cancel' : 'Close';
    const icon = state === 'started' ? <Loader size={16} color="gray.0" /> : undefined;

    useEffect(() => {
        if (!existingValue) {
            setOverwrite(false);
        }
    }, [existingValue]);

    return (
        <Stack>
            <SectionedPopoverToolbar>
                <Group position="right">
                    <Button onClick={onClick} color={addColor} disabled={addDisabled} leftIcon={icon}>
                        {addLbl}
                    </Button>
                    <Button onClick={onClose} variant="outline" disabled={cancelDisabled}>
                        {cancelLbl}
                    </Button>
                </Group>
            </SectionedPopoverToolbar>
            <TagKeyCombobox
                onChange={setKeyText}
                value={keyText}
                disabled={state === 'started'}
                inputProps={{
                    label: 'Label Key',
                    error: keyText ? keyError : undefined,
                }}
            />
            <TagValueCombobox
                onChange={setValueText}
                value={valueText}
                tagKey={keyText}
                disabled={state === 'started'}
                inputProps={{
                    label: 'Label Value',
                    error: valueText ? valueError : undefined,
                }}
            />
            {!existingValue ? null : (
                <Checkbox label={`Overwrite Existing "${keyText}" Label`} checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
            )}
            <InlineTaggingCreditBalance onOutOfCredits={setNoCredits} />
        </Stack>
    );
}
